import { Component, Input, OnInit, OnDestroy, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { Observable } from 'rxjs';
import { SubSink } from 'subsink';
import moment, { Moment } from 'moment';
import { IDashboardTask, ICaseWorkbenchTask } from '@CaseSummary/underwriting/models/uw-task.model';
import { UWTaskService } from '@Services/uw-task.service';
import { NotificationService, NotificationSeverity, ClientService } from '@Services';
import { UserSettingsService } from '@Services/global/user-settings.service';
import { Client, Account } from '@Models';
import { AppService } from '@App/app.service';
import { DashboardTasksDataService } from '@Services/dashboard-tasks.data.service';
import { DropdownOption } from '@Components/single-select-dropdown-list/single-select-dropdown-list.component';
import UWTasksList from '@CaseSummary/underwriting/components/tasks/components/uw-tasks-list/uw-tasks-list.component';
import { DashboardSlaDueDateFilters } from '../dashboard-sla-due-date-filters/dashboard-sla-due-date-filters.component';
import { DashboardFilterBarComponent, FilterBarState, FilterBarRangeConfig, DateRangeValue, emptyFilterBarState } from '../dashboard-filter-bar/dashboard-filter-bar.component';
import { SharedModule } from '@Shared/shared.module';

type DueDateKey = 'pastDue' | 'dueToday' | 'dueTomorrow';
type SlaDueDateCounts = Record<DueDateKey, number>;
const DUE_DATE_KEYS: ReadonlySet<DueDateKey> = new Set(['pastDue', 'dueToday', 'dueTomorrow']);

interface DashboardTasksFilterSettings {
    dueDateFilters: string[];
}

@Component({
    selector: 'dashboard-tasks-component',
    standalone: true,
    imports: [SharedModule, UWTasksList, DashboardSlaDueDateFilters, DashboardFilterBarComponent],
    providers: [
        DashboardTasksDataService,
        UWTaskService
    ],
    templateUrl: './dashboard-tasks.component.html',
    styleUrls: ['./dashboard-tasks.component.scss']
})
export class DashboardTasksComponent implements OnInit, OnDestroy, OnChanges {
    @Input() clientId: string;
    @Input() tasks: IDashboardTask[] = [];

    private _paginator?: MatPaginator;
    @ViewChild(MatPaginator)
    set paginator(p: MatPaginator) {
        this._paginator = p;
        this.paginatorRef = p;
    }
    get paginator(): MatPaginator | undefined {
        return this._paginator;
    }

    @ViewChild(DashboardSlaDueDateFilters)
    private readonly slaFilters?: DashboardSlaDueDateFilters;

    private static readonly GROUP_BY_CASE_KEY = 'dashboard-tasks:groupByCase';

    get groupByCase(): boolean {
        return sessionStorage.getItem(DashboardTasksComponent.GROUP_BY_CASE_KEY) === 'true';
    }
    set groupByCase(value: boolean) {
        sessionStorage.setItem(DashboardTasksComponent.GROUP_BY_CASE_KEY, String(value));
    }

    taskDueDateCounts: SlaDueDateCounts = {
        pastDue: 0,
        dueToday: 0,
        dueTomorrow: 0
    };
    filteredTasks: IDashboardTask[] = [];
    currentUserId: string;
    paginatorRef?: MatPaginator;

    filterState: FilterBarState = emptyFilterBarState();
    accountOptions: DropdownOption[] = [];
    private readonly accountsById = new Map<string, Account>();
    readonly dateRanges: FilterBarRangeConfig[] = [
        { key: 'followUp', label: 'Follow Up' },
        { key: 'dateAdded', label: 'Date Added' }
    ];

    private dueDateFilters: DueDateKey[] = [];
    private readonly subs = new SubSink();

    get dueDateFilterIds(): string[] {
        return [...this.dueDateFilters];
    }

    readonly loadAssignableUsers = (accountId: string, caseId: string): Observable<DropdownOption[]> =>
        this.dashboardTasksService.getAccountUsers(this.clientId, accountId, caseId, true);

    constructor(
        private readonly dashboardTasksService: DashboardTasksDataService,
        private readonly uwTaskService: UWTaskService,
        private readonly notificationService: NotificationService,
        private readonly appService: AppService,
        private readonly clientService: ClientService,
        private readonly userSettingsService: UserSettingsService
    ) {}

    ngOnInit(): void {
        this.currentUserId = this.appService.getUserId();
        this.loadAccounts();
        this.loadSavedFilters();
    }

    get activeFilterCount(): number {
        const f = this.filterState;
        const accountCount = f.accountIds.length > 0 ? 1 : 0;
        const followUpCount = this.isRangeActive('followUp') ? 1 : 0;
        const dateAddedCount = this.isRangeActive('dateAdded') ? 1 : 0;
        const searchCount = f.searchTerm.trim() ? 1 : 0;
        return this.dueDateFilters.length + accountCount + followUpCount + dateAddedCount + searchCount;
    }

    get itemCount(): number {
        if (!this.groupByCase) return this.filteredTasks.length;
        const caseNumbers = new Set<string>();
        this.filteredTasks.forEach(task => caseNumbers.add(task.caseInfo?.caseNumberDisplay ?? ''));
        return caseNumbers.size;
    }

    private getRange(key: string): DateRangeValue {
        return this.filterState.ranges[key] ?? { start: null, end: null };
    }

    private isRangeActive(key: string): boolean {
        const range = this.getRange(key);
        return !!(range.start || range.end);
    }

    handleDueDateFiltersEmitted(value: string): void {
        this.dueDateFilters = value
            ? value.split(',').filter((key): key is DueDateKey => DUE_DATE_KEYS.has(key as DueDateKey))
            : [];
        this.applyAllFilters();
        this.saveFilters();
    }

    onFiltersChange(state: FilterBarState): void {
        this.filterState = state;
        this.applyAllFilters();
    }

    onFiltersCleared(): void {
        this.filterState = emptyFilterBarState();
        this.dueDateFilters = [];
        this.slaFilters?.clearSelections();
        this.applyAllFilters();
        this.saveFilters();
    }

    private loadSavedFilters(): void {
        this.subs.add(
            this.userSettingsService.getSetting<DashboardTasksFilterSettings>(UserSettingsService.DASHBOARD_TASKS_FILTERS_KEY).subscribe(saved => {
                if (!saved?.dueDateFilters?.length) return;
                this.dueDateFilters = saved.dueDateFilters.filter((key): key is DueDateKey => DUE_DATE_KEYS.has(key as DueDateKey));
                this.applyAllFilters();
            })
        );
    }

    private saveFilters(): void {
        const settings: DashboardTasksFilterSettings = {
            dueDateFilters: [...this.dueDateFilters]
        };
        this.subs.add(this.userSettingsService.saveSetting(UserSettingsService.DASHBOARD_TASKS_FILTERS_KEY, settings).subscribe());
    }

    private refreshFilters(): void {
        this.buildFilterOptions();
        this.applyAllFilters();
    }

    private buildFilterOptions(): void {
        const accountMap = new Map<string, string>();

        this.tasks.forEach(task => {
            const accountId = task.caseInfo?.accountId;
            if (accountId && !accountMap.has(accountId)) {
                accountMap.set(accountId, this.resolveAccountLabel(task.caseInfo));
            }
        });

        this.accountOptions = Array.from(accountMap.entries())
            .map(([value, displayName]) => ({ value, displayName }))
            .sort((a, b) => a.displayName.localeCompare(b.displayName));
    }

    loadAccounts(): void {
        this.subs.add(
            this.clientService.getClientsAndAccounts(false, true)
                .subscribe((clients: Client[]) => {
                    this.indexAccounts(clients);
                    this.buildFilterOptions();
                })
        );
    }

    private indexAccounts(clients: Client[]): void {
        this.accountsById.clear();
        (clients || []).forEach(client => {
            (client.accounts || []).forEach(account => {
                this.indexAccount(account, client.code);
                (account.versions || []).forEach(version => this.indexAccount(version, client.code));
            });
        });
    }

    private indexAccount(account: Account, clientCode: string): void {
        if (account.id && !this.accountsById.has(account.id)) {
            this.accountsById.set(account.id, { ...account, clientCode: account.clientCode || clientCode });
        }
    }

    private resolveAccountLabel(c: any): string {
        const match = this.accountsById.get(c?.accountId);
        const name = (match?.name ?? c?.accountName)?.trim();
        const accountCode = (match?.code ?? c?.accountCode)?.trim();
        if (name && accountCode) return `${name} (${accountCode})`;
        return name || accountCode || '';
    }

    private isWithinRange(date: string | undefined, start: Moment | null, end: Moment | null): boolean {
        if (!date) return false;
        const parsed = moment(date);
        if (!parsed.isValid()) return false;
        const dateKey = parsed.format('YYYY-MM-DD');
        if (start && dateKey < start.format('YYYY-MM-DD')) return false;
        if (end && dateKey > end.format('YYYY-MM-DD')) return false;
        return true;
    }

    private taskMatchesSearch(task: IDashboardTask, term: string): boolean {
        const caseInfo = task.caseInfo;
        const fields = [
            task.name,
            caseInfo?.caseNumberDisplay,
            caseInfo?.casePolicyNumber,
            task.assignedTo,
            task.followUpDate ? this.formatSearchDate(task.followUpDate) : '',
            task.addedDate ? this.formatSearchDate(task.addedDate) : '',
        ];
        return fields.some(field => (field ?? '').toLowerCase().includes(term));
    }

    private formatSearchDate(date?: string): string {
        if (!date) return '';
        const parsed = moment.utc(date);
        return parsed.isValid() ? parsed.format('M/D/YYYY') : '';
    }

    private applyAllFilters(): void {
        let result = [...this.tasks];
        const f = this.filterState;

        if (this.dueDateFilters.length > 0) {
            result = result.filter(task => this.dueDateFilters.some(key => task[key]));
        }

        if (f.accountIds.length > 0) {
            result = result.filter(task => f.accountIds.includes(task.caseInfo?.accountId));
        }

        const followUp = this.getRange('followUp');
        if (followUp.start || followUp.end) {
            result = result.filter(task => this.isWithinRange(task.followUpDate, followUp.start, followUp.end));
        }

        const dateAdded = this.getRange('dateAdded');
        if (dateAdded.start || dateAdded.end) {
            result = result.filter(task => this.isWithinRange(task.addedDate ?? task.creationDate, dateAdded.start, dateAdded.end));
        }

        const term = f.searchTerm.trim().toLowerCase();
        if (term) {
            result = result.filter(task => this.taskMatchesSearch(task, term));
        }

        this.filteredTasks = result;
        this.updateDueDateCounts(this.filteredTasks);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tasks'] && !changes['tasks'].firstChange) {
            this.refreshFilters();
        }
    }

    onUpdateTask(event: { task: ICaseWorkbenchTask, message: string }): void {
        const updatedTask = event.task;
        const dashTask = updatedTask as unknown as IDashboardTask;
        const caseId = updatedTask.caseId || dashTask.caseInfo?.caseId;
        const clientId = updatedTask.clientId || this.clientId;
        if (!updatedTask.id || !caseId) {
            this.notificationService.showNotification({
                severity: NotificationSeverity.Error,
                message: 'Unable to save task: missing task or case identifier.',
                log: false
            });
            return;
        }
        const taskPayload: ICaseWorkbenchTask = {
            id: updatedTask.id,
            taskId: updatedTask.taskId,
            statusId: updatedTask.statusId,
            name: updatedTask.name,
            assigneeId: updatedTask.assigneeId ?? '',
            addedDate: updatedTask.addedDate ?? updatedTask.creationDate ?? '',
            creationDate: updatedTask.creationDate,
            completedDate: updatedTask.completedDate,
            followUpDate: updatedTask.followUpDate,
            hasAssociatedRequirement: updatedTask.hasAssociatedRequirement,
            associatedRequirementText: updatedTask.associatedRequirementText,
            clientId,
            caseId
        };
        this.subs.add(
            this.uwTaskService.updateUWTask(caseId, taskPayload.id, taskPayload).subscribe({
                next: () => {
                    this.notificationService.showNotification({
                        severity: NotificationSeverity.Success,
                        message: event.message,
                        log: false
                    });
                    this.tasks = this.tasks.map(t => t.id === taskPayload.id ? { ...t, ...taskPayload } : t);
                    this.refreshFilters();
                },
                error: () => {
                    this.notificationService.showNotification({
                        severity: NotificationSeverity.Error,
                        message: 'Failed to update task. Please try again later.',
                        log: true
                    });
                }
            })
        );
    }

    onTaskReassigned(taskId: string): void {
        if (!taskId) return;
        this.tasks = this.tasks.filter(t => t.id !== taskId);
        this.refreshFilters();
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    updateDueDateCounts(tasks: IDashboardTask[] = this.tasks): void {
        const counts: SlaDueDateCounts = { pastDue: 0, dueToday: 0, dueTomorrow: 0 };
        tasks.forEach(task => {
            DUE_DATE_KEYS.forEach(key => {
                if (task[key]) counts[key] += 1;
            });
        });
        this.taskDueDateCounts = counts;
    }
}

